<script setup>
// Step-by-step guidance to make this install a push device (#149), for the
// reachability banner and the "require push" block screen. `kind` comes from
// pushBanner() in services/reachability.js. iOS outside the home screen app
// gets install steps and no button: Safari cannot subscribe there.
import { computed, ref } from 'vue'
import { enablePushDevice } from '@/services/pushDevice.js'

const props = defineProps({
  kind: { type: String, required: true },
  openInSafari: { type: Boolean, default: false }
})
const emit = defineEmits(['enabled'])

const busy = ref(false)
const error = ref('')
const d = __DICT__.notifications

const steps = computed(() => {
  switch (props.kind) {
    case 'install':
      return [
        ...(props.openInSafari ? [d.stepOpenInSafari] : []),
        d.stepTapShare, d.stepAddToHomeScreen, d.stepOpenFromIcon, d.stepThenTurnOn
      ]
    case 'permission-denied':
      return [d.stepDeniedSettings, d.stepDeniedReturn]
    case 'unsupported':
      return [d.stepUnsupported]
    default:
      return [d.stepTapTurnOn, d.stepAllow]
  }
})
const showButton = computed(() => props.kind === 'enable' || props.kind === 'permission-denied')

async function turnOn() {
  error.value = ''
  busy.value = true
  try {
    const result = await enablePushDevice()
    if (result === 'registered') emit('enabled')
    else error.value = d.enableFailed
  } catch (e) {
    console.warn('[push device] enable failed', e?.message || e)
    error.value = d.enableFailed
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="push-setup-steps">
    <ol class="mb-2 ps-3">
      <li v-for="(step, i) in steps" :key="i" class="mb-1">{{ step }}</li>
    </ol>
    <button v-if="showButton" class="btn btn-primary w-100" :disabled="busy" @click="turnOn">
      <span v-if="busy" class="spinner-border spinner-border-sm me-2"></span>
      {{ d.turnOn }}
    </button>
    <div v-if="error" class="text-danger small mt-2">{{ error }}</div>
  </div>
</template>

<style scoped>
.push-setup-steps {
  font-size: 0.875rem;
}
</style>
